import React from 'react';
import { Form, Field, useField } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ARRAY_ERROR } from 'final-form';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import { Row, Col, CardBody, Card, CustomInput } from 'reactstrap';

import FormField, { FormArrayError } from 'components/Form/FormField';
import { formArrayValidate, required } from 'utils/formValidators';

import { NPSScoreAnswer, CommentTypeAnswer } from '../answers/answerTypes';

const RenderButton = ({ handleClick, title, className, disabled }) => {
  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={disabled}
    >
      {title}
    </button>
  );
};

const DefaultQuestionView = () => (
  <Col className="pl-5 pt-3">
    Examples:
    <ul className="pl-0">
      <li>Overall how would you rate your visit?</li>
      <li>Why did you purchase this [product]? </li>
      <li>How satisfied are you with [product]?</li>
    </ul>
  </Col>
);

const AnswersView = ({
  initialStep,
  handleInputTypeSelection,
  inputType,
  reselectInputType,
  addAnswer,
  pristine,
  submitting,
  inputTypes,
  editForm,
}) => {
  if (initialStep) return <DefaultQuestionView />;

  if (inputType) {
    return (
      <InputTypeWrapper
        reselectInputType={reselectInputType}
        editForm={editForm}
      >
        <DisplayAnswers
          inputType={inputType}
          addAnswer={addAnswer}
          pristine={pristine}
          submitting={submitting}
        />
      </InputTypeWrapper>
    );
  }

  return (
    <SelectInputType
      inputTypes={inputTypes}
      handleInputTypeSelection={handleInputTypeSelection}
    />
  );
};

const SelectInputType = ({ handleInputTypeSelection, inputTypes }) => {
  const inputTypesList = inputTypes.map(type => (
    <InputType type={type} handleClick={() => handleInputTypeSelection(type)} />
  ));

  return (
    <div className="pl-3 pt-3">
      Select an answer Type
      <div className="d-flex flex-row flex-wrap">{inputTypesList}</div>
    </div>
  );
};

const InputType = ({ type, handleClick }) => (
  <div
    role="presentation"
    className="border py-2 px-4 m-2 cursor-pointer"
    onClick={handleClick}
    style={{ width: '15em' }}
  >
    <FontAwesomeIcon color="#28518b" icon="chart-pie" />
    {type.display_name}
  </div>
);

const DisplayAnswers = ({ inputType, addAnswer, pristine, submitting }) => {
  if (inputType.id === 3) return <CommentTypeAnswer />;
  if (inputType.id === 4) return <NPSScoreAnswer />;
  return (
    <AnswerInputField
      addAnswer={addAnswer}
      pristine={pristine}
      submitting={submitting}
      inputType={inputType}
    />
  );
};

const InputTypeWrapper = ({ children, reselectInputType, editForm }) => {
  if (editForm) return children;
  return (
    <div>
      {children}
      <button className="btn btn-primary" onClick={reselectInputType}>
        Change Answer Type
      </button>
    </div>
  );
};

const makeOnDragEndFunction = fields => result => {
  if (!result.destination) {
    return;
  }
  fields.swap(result.source.index, result.destination.index);
};

const AnswerButtons = ({
  provided,
  name,
  onDelete,
  isEditing,
  onCancel,
  onChange,
}) => {
  if (isEditing) {
    return (
      <span className="answer-options">
        <span onClick={onChange} className="cursor-pointer">
          <FontAwesomeIcon icon="check" />
        </span>
        <span role="presentation" onClick={onCancel} className="cursor-pointer">
          <FontAwesomeIcon icon="times" />
        </span>
      </span>
    );
  }
  return (
    <span className="answer-options">
      <span {...provided.dragHandleProps}>
        <FontAwesomeIcon icon="arrows-alt" />
      </span>
      <AnswerEditButton name={name} />
      <span role="presentation" onClick={onDelete} className="cursor-pointer">
        <FontAwesomeIcon icon="trash-alt" />
      </span>
    </span>
  );
};

const AnswerField = ({
  name,
  isEditing,
  provided,
  fields,
  index,
  inputType,
}) => {
  const fieldName = isEditing ? `${name}.editAnswer` : `${name}.answer`;
  const editField = useField(`${name}.editAnswer`);
  const defaultField = useField(`${name}.answer`);
  const isEditingField = useField(`${name}.edit`);

  const htmlElement = inputType.html_element;
  const onDelete = () => fields.remove(index);
  const onChange = () => {
    defaultField.input.onChange(editField.input.value);
    isEditingField.input.onChange(false);
  };

  const onCancel = () => {
    editField.input.onChange(defaultField.input.value);
    isEditingField.input.onChange(false);
  };

  return (
    <Row className="my-2">
      <CustomInput
        type={htmlElement}
        className="align-self-end"
        name={fieldName}
        value="defaultRadio"
        checked
      />
      <FormField
        name={fieldName}
        placeholder="Option"
        disabled={!isEditing}
        inputClassName="form-control"
        containerClassName="col-10 pl-0 py-1"
        validate={required}
      />
      <AnswerButtons
        isEditing={isEditing}
        provided={provided}
        name={name}
        onDelete={onDelete}
        onChange={onChange}
        onCancel={onCancel}
      />
    </Row>
  );
};

const AnswerEditButton = ({ name }) => (
  <Field
    name={`${name}.edit`}
    render={({ input }) => (
      <span role="presentation" onClick={() => input.onChange(!input.value)}>
        <FontAwesomeIcon icon="pencil-alt" />
      </span>
    )}
  />
);

const AnswerInputField = ({ addAnswer, pristine, submitting, inputType }) => (
  <React.Fragment>
    <FieldArray
      name="answer_attributes"
      validateOnBlur
      validate={formArrayValidate}
    >
      {({ fields }) => {
        return (
          <DragDropContext onDragEnd={makeOnDragEndFunction(fields)}>
            <Droppable key={fields.length} droppableId="droppable">
              {provided => (
                <div ref={provided.innerRef}>
                  {fields.map((name, index) => {
                    const inputField = fields.value[index];
                    const isEditing = inputField.edit;

                    return (
                      <Draggable
                        key={name}
                        draggableId={name}
                        isDragDisabled={isEditing}
                        index={index}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                        }}
                      >
                        {provided => (
                          <div
                            className="answer-container"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <AnswerField
                              key={name}
                              name={name}
                              isEditing={isEditing}
                              provided={provided}
                              fields={fields}
                              index={index}
                              inputType={inputType}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  <FormArrayError name="answer_attributes" />
                  {fields.length >= 10 && (
                    <div className="row error">Maximum 10 options</div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        );
      }}
    </FieldArray>
    <Row>
      <Col md="12" className="position-relative">
        <div className="w-100 input-group">
          <FormField
            name="add_answer"
            containerClassName="w-75"
            placeholder="Type your option here..."
            inputClassName="form-control"
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                return addAnswer();
              }
            }}
          />

          <div className="input-group-append">
            <RenderButton
              className="btn btn-light btn-add-answer"
              handleClick={addAnswer}
              disabled={submitting || pristine}
              title={
                <React.Fragment>
                  <FontAwesomeIcon icon="plus" />
                  Add Answer
                </React.Fragment>
              }
            />
          </div>
        </div>
      </Col>
    </Row>
  </React.Fragment>
);

const SuggestedQuestions = ({
  suggestedQuestions,
  handleSuggestionSelected,
}) => {
  if (!suggestedQuestions.length) {
    return null;
  }
  return suggestedQuestions.map(question => (
    <div
      className="col suggested-question cursor-pointer bg-white shadow"
      onClick={() => handleSuggestionSelected(question)}
      role="button"
      tabIndex={0}
      onKeyPress={() => {}}
    >
      {question.question}
    </div>
  ));
};

const QuestionForm = ({
  inputType,
  question,
  currentStep,
  toggleForm,
  toggleSuggestions,
  suggestedQuestions,
  inputTypes,
  handleSubmit,
  handleNext,
  fetchSuggestedQuestions,
  handleSuggestionSelected,
  setInputType,
  editForm,
}) => {
  return (
    <Form
      onSubmit={inputType ? handleSubmit : handleNext}
      mutators={{
        ...arrayMutators,
      }}
      keepDirtyOnReinitialize
      initialValues={question}
      validate={values => {
        const errors = { answer_attributes: [] };
        if (values.answer_attributes && values.answer_attributes.length > 10) {
          errors.answer_attributes[ARRAY_ERROR] = 'Maximum 10 options only';
        }
        return errors;
      }}
      render={({
        handleSubmit,
        form: {
          mutators: { push },
        },
        pristine,
        form,
        submitting,
        values,
      }) => {
        const addAnswer = () => {
          if (
            values.answer_attributes &&
            values.answer_attributes.length >= 10
          ) {
            return;
          }
          if (values.add_answer) {
            push('answer_attributes', {
              answer: values.add_answer,
              editAnswer: values.add_answer,
            });
            form.change('add_answer', '');
          }
        };

        return (
          <Card className="question-answer-container">
            <CardBody className="">
              <form onSubmit={handleSubmit}>
                <Row>
                  <Col md="10" className="position-relative">
                    <FormField
                      name="question"
                      plcaeholder="Type your question here..."
                      inputClassName="form-control"
                      validate={required}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          return handleSubmit(values);
                        }
                      }}
                      onChangeCallback={fetchSuggestedQuestions}
                    />
                  </Col>
                  <Col>
                    {currentStep === 0 ? (
                      <RenderButton
                        className="btn"
                        handleClick={handleSubmit}
                        title="Continue"
                      />
                    ) : (
                      <div>
                        <RenderButton
                          className="circular-icon icon-check ml-0"
                          handleClick={handleSubmit}
                          disabled={submitting || !inputType}
                          title={<FontAwesomeIcon icon="check" />}
                        />
                        <RenderButton
                          className="circular-icon icon-cross"
                          handleClick={toggleForm}
                          disabled={submitting}
                          title={<FontAwesomeIcon icon="times" />}
                        />
                      </div>
                    )}
                  </Col>
                  <div className="w-100 suggested-questions">
                    <SuggestedQuestions
                      toggleSuggestions={toggleSuggestions}
                      suggestedQuestions={
                        currentStep === 0 ? suggestedQuestions : []
                      }
                      handleSuggestionSelected={question => {
                        form.change('question', question.question);
                        handleSuggestionSelected(question);
                      }}
                    />
                  </div>
                </Row>
                <AnswersView
                  key={currentStep}
                  initialStep={currentStep === 0}
                  handleInputTypeSelection={setInputType}
                  inputType={inputType}
                  reselectInputType={() => setInputType()}
                  addAnswer={addAnswer}
                  pristine={pristine}
                  submitting={submitting}
                  inputTypes={inputTypes}
                  editForm={editForm}
                />
              </form>
            </CardBody>
          </Card>
        );
      }}
    />
  );
};

export default QuestionForm;



// WEBPACK FOOTER //
// ./src/components/questions/form/questionForm.js